/**
 * ---------------------------------------------------------------------------
 * Vue Router Configuration (Shell Application)
 * ---------------------------------------------------------------------------
 * This file defines the complete routing setup for the Vue 3 shell application.
 *
 * The shell hosts multiple Micro Frontends (MFEs) and shared preview pages.
 * It also handles reserved paths for embedded MFEs, application dashboards,
 * and fallback pages such as "Unavailable" and "Not Found".
 *
 * @author: Harvinder Singh <harvinder@maropost.com>
 * @created: 2025-09-12
 * ---------------------------------------------------------------------------
 */

import { createRouter, createWebHistory } from "vue-router"
import routes from "./routeList"
import { useUserStore } from "../stores"
import { navigateToUrl } from "single-spa"

import { canAccessRoute } from "./middleware/route-access"
import { fetchAccountIdFromUrl } from "../utils/accounts"
import { updateDocumentTitle } from "./middleware/documentTitle"


// -----------------------------------------------------------------------------
// Router Instance
// -----------------------------------------------------------------------------
// Using HTML5 history mode for clean URLs (no #hash).
// The base "/" ensures proper linking and navigation under root deployment.
const router = createRouter({
  history: createWebHistory("/"),
  routes,
})

// -----------------------------------------------------------------------------
// Global Navigation Guards
// -----------------------------------------------------------------------------
router.beforeEach(async (to, _from, next) => {
  // Prepare document title
  updateDocumentTitle(to)

  // Get user store instance (contains user/account-related state)
  const userStore = useUserStore()

  // Show CTABanner
  userStore.setCTABanner(true);

  // Extract accountId from current URL
  const accountIdFromUrl = fetchAccountIdFromUrl()

  // Check if URL accountId matches the logged-in user's accountId
  if (Number.parseInt(accountIdFromUrl) == userStore.getAccountId) {

    // Determine if the account is a trial account AND trial has expired
    const isTrialExpired =
      userStore.getIsTrailAccount && userStore.getIsTrialExpired

    if (isTrialExpired) {

      // If user is NOT already on billing/subscription page,
      // redirect them to subscription page
      if (!to.fullPath.includes('account-&-billing')) {

        navigateToUrl(
          `/accounts/${userStore.getAccountId}/account-&-billing/subscriptions`
        )

        // Continue navigation after redirect trigger
        next()
        return
      }

      // If already on billing page, allow navigation
      next()
      return
    }
  }

  // Check if user has access to the requested route (feature-based access control)
  const hasAccess = await canAccessRoute(to.path, userStore)

  if (hasAccess) {
    // Allow navigation if user has access
    return next()
  }

  // Default fallback: allow navigation (can be replaced with 403/redirect if needed)
  next()
})

// -----------------------------------------------------------------------------
// Export Router
// -----------------------------------------------------------------------------
export default router
